import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import LoginForm from './components/LoginForm';
import ProductsManager from './components/ProductsManager';
import CategoriesManager from './components/CategoriesManager';
import GuidesManager from './components/GuidesManager';
import ComparisonsManager from './components/ComparisonsManager';

type Tab = 'products' | 'categories' | 'guides' | 'comparisons';

const tabs: { key: Tab; label: string; icon: string }[] = [
  { key: 'products', label: 'Products', icon: 'ri-shopping-bag-line' },
  { key: 'categories', label: 'Categories', icon: 'ri-price-tag-3-line' },
  { key: 'guides', label: 'Guides', icon: 'ri-book-open-line' },
  { key: 'comparisons', label: 'Comparisons', icon: 'ri-scales-3-line' },
];

export default function Admin() {
  const [session, setSession] = useState<boolean | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('products');
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(!!s);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(!!s);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin');
  };

  // Still loading auth state
  if (session === null) {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center">
        <div className="text-ink-500 text-sm">Loading...</div>
      </div>
    );
  }

  // Not logged in
  if (!session) {
    return <LoginForm onLogin={() => setSession(true)} />;
  }

  // Logged in: show dashboard
  return (
    <div className="min-h-screen bg-ink-50 text-ink-900 antialiased">
      {/* Admin Header */}
      <header className="bg-white border-b border-ink-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="flex items-center gap-2" target="_blank" rel="noopener noreferrer">
              <img src="/buywize-logo.png" alt="BuyWize" className="h-7 w-auto" />
            </a>
            <span className="h-5 w-px bg-ink-200"></span>
            <span className="text-sm font-semibold text-ink-700">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noopener noreferrer" className="h-8 px-3 rounded-lg text-xs font-semibold text-ink-600 hover:bg-ink-50 whitespace-nowrap transition flex items-center gap-1">
              View Site <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-external-link-line"></i></span>
            </a>
            <button onClick={handleLogout} className="h-8 px-3 rounded-lg text-xs font-semibold text-rose-600 hover:bg-rose-50 whitespace-nowrap transition">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-ink-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-0 -mb-px">
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap transition border-b-2 ${
                  activeTab === t.key
                    ? 'border-ink-900 text-ink-900'
                    : 'border-transparent text-ink-500 hover:text-ink-700'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={t.icon}></i>
                </span>
                {t.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {activeTab === 'products' && <ProductsManager />}
        {activeTab === 'categories' && <CategoriesManager />}
        {activeTab === 'guides' && <GuidesManager />}
        {activeTab === 'comparisons' && <ComparisonsManager />}
      </main>
    </div>
  );
}